// RecordingStudio — full-screen Recording Booth overlay (Phase 6 Task 2).
//
// Per spec `2026-04-22-sts-recording-booth-design.md`:
//   Left (~50%):  Video reference, Record + Upload buttons, Script (scrollable)
//   Middle (~25%): STS params (transient, reset on open)
//   Right (~25%): Takes list (up to 5, checkmark = keep)
//
// Flow (spec §Recording Studio Overlay):
//   1. Open → empty takes list, default params
//   2. Optional params adjust
//   3. [● Record] → 3-2-1 preroll (1kHz sine) → recorder starts
//   4. [■ Stop]   → stop recorder → generateSts() → take card appears
//   5. Talent plays/marks/deletes takes
//   6. [Finish Recording] → marked takes emitted to parent; unmarked
//      takes' blob URLs revoked + ArrayBuffers dropped
//
// Design decisions:
//  - Dedicated inline <video> element (not <VideoPlayer />, not a portal of
//    the main-screen one). Reason: the main-screen VideoPlayer owns the
//    setActiveVideo singleton used by timeline preview / ducking; portaling
//    it would break that contract. This overlay's video is visual-reference-
//    only — no sync, no upload UI — so a slim inline element is both
//    simpler and correct.
//  - Transient state only. The only escape route for takes is the explicit
//    Finish callback. Closing or Cancel drops everything.
//  - Blob URL lifecycle: each take gets an input Blob URL (for potential
//    playback of the source recording — not shown in UI yet, reserved for
//    future re-process). STS output uses its own blob URL per the Waveform
//    component's internal handling.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { STSParams, STSTake } from '@shared/types';
import {
  RECORDING_SESSION_TAKE_LIMIT,
  STS_PARAMS_DEFAULT,
} from '@shared/constants';
import { formatTakeLabel } from '@/lib/stsTakeLabel';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ToastProvider';
import { streamVideoUrl } from '@/lib/api';
import { useSessionStore } from '@/stores/session';
import { buildAssetList } from '@/lib/assetList';
import { useVideoStore } from '@/stores/video';
import { getPrimaryVoiceId } from '@/lib/voiceMigration';
import {
  BoothDeviceControls,
  BoothFooter,
  BoothMidColumn,
  BoothTakesColumn,
  BoothVideoPanel,
} from './BoothPanels';
import { useStsGenerate } from './useStsGenerate';
import { useBoothRecording } from './useBoothRecording';
import type { DraftTake, LoadedSource } from './boothTypes';
import styles from './RecordingStudio.module.css';
import { scriptAttrs } from '@/lib/scriptDetect';

export interface RecordingStudioProps {
  /** When true, overlay renders. When false, nothing is rendered. */
  open: boolean;
  /** Called when the user closes the overlay via Cancel, Esc, or the close-X.
   *  Any unpersisted takes are discarded. */
  onClose(): void;
  /** Called when the user clicks [Finish Recording]. Receives only the
   *  marked takes — unmarked ones are already being torn down by the
   *  overlay when this fires. */
  onFinish(takes: STSTake[]): void;
}

export function RecordingStudio({ open, onClose, onFinish }: RecordingStudioProps) {
  const toolId = useSessionStore((s) => s.toolId);
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const configAssets = useSessionStore((s) => s.config?.assets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const customAssets = useSessionStore((s) => s.customAssets);
  const videoBlobs = useVideoStore((s) => s.blobs);
  const videoSources = useVideoStore((s) => s.sources);
  const videoDurations = useVideoStore((s) => s.durations);

  // `buildAssetList` tags custom assets with `_isCustom` — the previous
  // hand-rolled spread dropped the tag, so `videoEnabled` fell back to
  // `allowVideo` (never set on custom assets) and the talent's own video
  // never showed in the overlay (GitHub #1, point 4).
  const activeAsset = useMemo(
    () => buildAssetList(configAssets, configVoices, customAssets)[currentAssetIdx],
    [configAssets, configVoices, customAssets, currentAssetIdx],
  );
  // Multi-voice (#173) Phase 1: pre-Phase-5 default is voiceIds[0]. Every
  // freshly-recorded take gets stamped with this so the timeline track
  // assignment is deterministic.
  const activeAssetVoiceId = getPrimaryVoiceId(activeAsset);
  // Multi-voice (#173) Phase 6: the asset-level voice list drives the
  // header VoicePicker. Empty array is treated as single-voice (picker
  // hidden) — `activeAssetVoiceId` is the only target for generates.
  const assetVoiceIds = useMemo<string[]>(
    () => activeAsset?.voiceIds ?? [],
    [activeAsset],
  );
  const isMultiVoice = assetVoiceIds.length >= 2;

  // Predefined assets honour admin `allowVideo`; custom assets always have
  // the Video Reference panel available (no per-asset opt-in toggle), so a
  // loaded blob or admin path is enough to drive the recording-focus video.
  const videoEnabled = useMemo(() => {
    if (!activeAsset) return false;
    if (activeAsset._isCustom) return true;
    return !!activeAsset.allowVideo;
  }, [activeAsset]);

  const videoSrc: string | null = useMemo(() => {
    if (!videoEnabled) return null;
    if (videoBlobs[currentAssetIdx]) return videoSources[currentAssetIdx] ?? null;
    if (activeAsset?.videoPath && toolId) {
      return streamVideoUrl(toolId, activeAsset.videoPath);
    }
    return null;
  }, [
    videoEnabled,
    videoBlobs,
    videoSources,
    currentAssetIdx,
    activeAsset,
    toolId,
  ]);

  const toast = useToast();

  const [params, setParams] = useState<STSParams>({ ...STS_PARAMS_DEFAULT });
  // Multi-voice (#173) Phase 6: the picker-selected target voice for the
  // NEXT generate. Initialised to the asset's primary voice on overlay open
  // and reset on re-open via the open-effect below. Single-voice assets
  // never read this state — `activeAssetVoiceId` is used instead.
  const [currentVoiceId, setCurrentVoiceId] = useState<string>(
    () => getPrimaryVoiceId(activeAsset),
  );
  // Multi-voice (#173) Phase 6: a "loaded source" is the trimmed-and-ready
  // blob that came either from the recorder (after Stop) or from a file
  // upload — but has NOT yet been generated through the STS API. The
  // explicit [Generate] button consumes this. Stays loaded across voice
  // switches so the user can re-generate the same source with a different
  // voice (per spec — voice-keyed take slots in the same recording session).
  const [loadedSource, setLoadedSource] = useState<LoadedSource | null>(null);
  const [takes, setTakes] = useState<DraftTake[]>([]);
  // Which take card is currently playing (for mutual-exclusion like main screen).
  const [playingTakeId, setPlayingTakeId] = useState<string | null>(null);
  // Session number for this overlay open, stamped on every take recorded
  // here. Bumped in the asset's STSAssetState the moment the overlay opens.
  // null until claimed (one tick after `open` flips true).
  const [sessionNum, setSessionNum] = useState<number | null>(null);
  // Volume of the inline reference video's audio track (0–1). Talent-tunable
  // so the original VO can be lowered/muted while recording. Persists across
  // overlay open/close and across asset navigation — value applies to the
  // <video> via an effect that re-runs when `videoVolume` or `videoSrc`
  // changes. Default 0.25 — quiet by default so the talent isn't startled
  // and the headphone hint is the path to "louder if needed".
  const [videoVolume, setVideoVolume] = useState(0.25);
  // Top-right time overlay state. Duration comes from the shared video
  // store (the main-page VideoPlayer writes it on loadedmetadata, so by
  // the time the recording booth opens the duration is already known —
  // no race with the booth's own <video> element). Current playback
  // position is read off the inline <video> via rAF since the booth's
  // playhead is independent of the main page's.
  const [videoTime, setVideoTime] = useState(0);
  const videoDuration = videoDurations[currentAssetIdx] ?? 0;

  const beginStsRecordingSession = useSessionStore(
    (s) => s.beginStsRecordingSession,
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  // Apply the user's chosen volume to the inline reference video. Re-runs
  // when `videoVolume` changes OR when `videoSrc` changes (asset nav swaps
  // the <video> source — the element doesn't unmount, but a fresh source
  // resets the underlying media's volume to 1, so we need to re-apply).
  // `open` is in deps because RecordingStudio is mounted permanently by
  // STSView (with `open=false`); the <video> only enters the DOM when
  // open=true, so the effect must re-run on that transition to find the
  // ref attached.
  useEffect(() => {
    const v = videoElRef.current;
    if (!v) return;
    v.volume = videoVolume;
  }, [videoVolume, videoSrc, open]);

  // When the active asset's video source changes (asset navigation) reset
  // the playhead to 0 and pause — there's no playback control besides
  // record/stop, so a fresh asset starts from the beginning.
  useEffect(() => {
    const v = videoElRef.current;
    if (!v) return;
    try { v.pause(); } catch { /* ignore */ }
    try { v.currentTime = 0; } catch { /* ignore — element may not be ready */ }
    setVideoTime(0);
  }, [videoSrc, open]);

  // Time-overlay sync. Brute-force setInterval reading currentTime off the
  // ref every 100ms (set + clear, no captured-stale-ref risk). Also wires
  // loadeddata for a tiny seek to force Safari to paint the first frame.
  // `open` MUST be in deps: see comment on the volume effect above — the
  // <video> only mounts when the overlay opens, so without `open` here the
  // effect would fire once on STSView mount (ref still null, early-return)
  // and never again, leaving the time overlay stuck at 0.
  useEffect(() => {
    if (!open) return;
    const v = videoElRef.current;
    if (!v || !videoSrc) return;
    const onLoadedData = () => {
      try { v.currentTime = 0.001; } catch { /* ignore */ }
    };
    v.addEventListener('loadeddata', onLoadedData);
    const id = window.setInterval(() => {
      const el = videoElRef.current;
      if (!el) return;
      setVideoTime(el.currentTime);
    }, 100);
    return () => {
      window.clearInterval(id);
      v.removeEventListener('loadeddata', onLoadedData);
    };
  }, [videoSrc, open]);

  // Reset overlay-scoped state whenever the overlay opens afresh. Spec §3:
  // "Each [+ New Recording Session] opens a fresh empty overlay."
  useEffect(() => {
    if (!open) return;
    setParams({ ...STS_PARAMS_DEFAULT });
    // Reset picker to the asset's primary voice on every open — strictly UI
    // state, not persisted between opens (#173 Phase 6 spec).
    setCurrentVoiceId(getPrimaryVoiceId(activeAsset));
    // Drop any source from a previous (unfinished) open — each "+ New
    // Recording Session" is a fresh slate (#173 Phase 6).
    setLoadedSource(null);
    setTakes([]);
    setPlayingTakeId(null);
    // Park the reference video at frame 0 — recording is the only thing
    // that plays it now (the native <video controls> were removed so the
    // talent can't accidentally desync it from a take).
    const v = videoElRef.current;
    if (v) {
      try { v.pause(); } catch { /* ignore */ }
      try { v.currentTime = 0; } catch { /* ignore */ }
    }
    // Reset session number — it's now claimed per-recording (not per-open)
    // so two different recordings within the same overlay open produce
    // distinct take slots even with the same voice (#173 follow-up).
    setSessionNum(null);
    // `activeAsset` participates only via the initial currentVoiceId reset
    // — re-running this effect on every config-change while open is fine,
    // it just re-claims a session number (cheap) and refreshes UI defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, beginStsRecordingSession, currentAssetIdx]);

  // Mic / recorder control of the booth (useBoothRecording.ts): permission
  // pre-prompt + device list, preroll countdown, record / stop, interruption
  // handling. Called AFTER the open-reset effect above so the effect order
  // (video → reset → recorder cleanup → permission → devicechange → device
  // fallback) is unchanged.
  const hasVoice = !!activeAssetVoiceId;
  const atLimit = takes.length >= RECORDING_SESSION_TAKE_LIMIT;
  const {
    state,
    setState,
    busy,
    countdown,
    permState,
    inputDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    requestMicPermission,
    startRecording,
    stopRecording,
    recordingRef,
  } = useBoothRecording({
    open,
    toolId,
    currentAssetIdx,
    beginStsRecordingSession,
    hasVoice,
    atLimit,
    videoElRef,
    toast,
    setPlayingTakeId,
    setLoadedSource,
    setSessionNum,
  });

  // Escape-to-close with confirm if there are unmarked takes that would be lost.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      handleCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, takes.length]);

  // Revoke a single take's blob URLs. Called on individual delete + on cancel.
  const revokeTake = (t: DraftTake) => {
    try { URL.revokeObjectURL(t.outputUrl); } catch { /* ignore */ }
    try { URL.revokeObjectURL(t.inputUrl); } catch { /* ignore */ }
  };

  // ── Record / Upload ─────────────────────────────────────────────────────
  // Snapshot of current takes for the confirm-then-mutate flow + the
  // session-take counter on creation. Reading from a ref keeps record/upload
  // callbacks free of `takes` in their deps (otherwise the Waveform on a
  // TakeCard rebuilds on every parent setTakes via prop-identity churn).
  const takesRef = useRef(takes);
  takesRef.current = takes;

  // Phase 6 (#173 Task 6.3): explicit Generate with voice-keyed take slots
  // (useStsGenerate.ts).
  const runStsGenerate = useStsGenerate({
    toolId,
    loadedSource,
    currentVoiceId,
    activeAssetVoiceId,
    sessionNum,
    params,
    takesRef,
    setTakes,
    setState,
    setPlayingTakeId,
    toast,
  });

  const onUploadClick = () => {
    if (!hasVoice || atLimit || busy) return;
    fileInputRef.current?.click();
  };

  // Phase 6 (#173): upload now mirrors record — file becomes the loaded
  // source, the explicit Generate button triggers the STS call. No
  // generating state set here, just a quick `setLoadedSource`.
  const onUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-pick of the same file
    if (!file || !toolId) return;
    const mime = file.type || 'audio/mpeg';
    setLoadedSource({ blob: file, mime, label: file.name });
    // New input → new session (mirror of stopRecording).
    setSessionNum(beginStsRecordingSession(currentAssetIdx));
  };

  // ── Take-card actions ───────────────────────────────────────────────────
  const toggleMark = useCallback((id: string) => {
    setTakes((prev) =>
      prev.map((t) => (t.id === id ? { ...t, marked: !t.marked } : t)),
    );
  }, []);

  const deleteTake = useCallback((id: string) => {
    // Confirm OUTSIDE setState — React StrictMode invokes state updaters
    // twice in dev to surface impure-update bugs, so `window.confirm`
    // inside the updater would prompt twice.
    const idx = takesRef.current.findIndex((t) => t.id === id);
    if (idx === -1) return;
    const t = takesRef.current[idx];
    const label = formatTakeLabel(t, idx);
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    // Window.confirm suspends the page's AudioContext on Safari; if the
    // shared context was still draining a preroll oscillator that hadn't
    // ramped to silence yet, dismissing the dialog resumes the context and
    // the oscillator fires as a stray beep. Stop any scheduled preroll
    // oscillators eagerly (this does NOT close the shared context).
    recordingRef.current.cancelPreroll();
    const gone = takesRef.current[idx];
    if (gone) revokeTake(gone);
    setTakes((prev) => prev.filter((t) => t.id !== id));
    setPlayingTakeId((cur) => (cur === id ? null : cur));
  }, [recordingRef]);

  // ── Close paths ─────────────────────────────────────────────────────────
  const hasUnmarked = takes.some((t) => !t.marked);

  const handleCancel = () => {
    if (takes.length > 0) {
      const ok = window.confirm(
        takes.length === 1
          ? 'Discard the recorded take and close?'
          : `Discard all ${takes.length} recorded takes and close?`,
      );
      if (!ok) return;
    }
    // Pause + rewind the reference video on close. The element unmounts
    // anyway when the overlay closes (open=false short-circuits render),
    // but pausing first avoids a stray frame of audio if MediaRecorder is
    // mid-stop while the user clicks Cancel.
    const v = videoElRef.current;
    if (v) {
      try { v.pause(); } catch { /* ignore */ }
      try { v.currentTime = 0; } catch { /* ignore */ }
    }
    takes.forEach(revokeTake);
    setTakes([]);
    onClose();
  };

  const handleFinish = () => {
    const marked = takes.filter((t) => t.marked);
    const unmarked = takes.filter((t) => !t.marked);
    // Revoke unmarked-takes' blob URLs BEFORE calling onFinish (parent has
    // already captured the ArrayBuffer references, but we want to be
    // explicit about the teardown ordering).
    unmarked.forEach(revokeTake);
    // The marked takes' blob URLs are revoked too — the parent re-builds
    // URLs from the ArrayBuffer when it renders the take on the main screen
    // (via Waveform, which creates its own URL).
    marked.forEach(revokeTake);
    // Strip overlay-only fields before emitting. Persistent session/take
    // numbers ride along so the master timeline can render `Take {s}-{n}`.
    const committed: STSTake[] = marked.map((t) => ({
      id: t.id,
      audioBuffer: t.audioBuffer,
      inputBuffer: t.inputBuffer,
      inputMime: t.inputMime,
      duration: t.duration,
      createdAt: t.createdAt,
      params: t.params,
      voiceId: t.voiceId,
      session: t.session,
      takeInSession: t.takeInSession,
    }));
    setTakes([]);
    onFinish(committed);
  };

  if (!open) return null;

  // ── Render ──────────────────────────────────────────────────────────────
  const body = (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.shell}>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={handleCancel}
          title="Close STS Recording"
          aria-label="Close STS Recording"
        >
          <i className="ph-bold ph-x" />
        </button>

        <div className={styles.title}>
          <i className="ph-fill ph-microphone" />
          <span>STS Recording</span>
          {activeAsset?.title && (
            <span className={styles.titleSub}>— <span {...scriptAttrs(activeAsset.title)}>{activeAsset.title}</span></span>
          )}
        </div>

        <div className={styles.columns}>
          {/* ── Left column (~50%) ─────────────────────────────────────── */}
          <div className={styles.colLeft}>
            <BoothVideoPanel
              videoSrc={videoSrc}
              videoRef={videoElRef}
              videoTime={videoTime}
              videoDuration={videoDuration}
              countdown={countdown}
              stateKind={state.kind}
            />

            <BoothDeviceControls
              permState={permState}
              inputDevices={inputDevices}
              selectedDeviceId={selectedDeviceId}
              onSelectDevice={setSelectedDeviceId}
              onRetryPermission={requestMicPermission}
              busy={busy}
              videoSrc={videoSrc}
              videoVolume={videoVolume}
              setVideoVolume={setVideoVolume}
            />

            <div className={styles.actionsRow}>
              {state.kind === 'recording' ? (
                <Button
                  variant="primary"
                  size="sm"
                  className={styles.recordBtnStop}
                  onClick={stopRecording}
                >
                  <i className="ph-fill ph-stop" /> Stop
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={
                    !hasVoice ||
                    atLimit ||
                    busy ||
                    permState.kind !== 'granted'
                  }
                  onClick={startRecording}
                  title={
                    !hasVoice
                      ? 'No voice assigned to this asset'
                      : atLimit
                        ? 'Delete a take to record again'
                        : permState.kind === 'denied'
                          ? 'Grant microphone access to record'
                          : permState.kind === 'pending'
                            ? 'Waiting for microphone access…'
                            : undefined
                  }
                >
                  <i className="ph-fill ph-record" /> Record
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                disabled={!hasVoice || atLimit || busy}
                onClick={onUploadClick}
                title={
                  !hasVoice
                    ? 'No voice assigned to this asset'
                    : atLimit
                      ? 'Delete a take to upload again'
                      : undefined
                }
              >
                <i className="ph-bold ph-upload-simple" /> Upload File
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                style={{ display: 'none' }}
                onChange={onUploadFile}
              />

              {/* Headphone hint — same row as Record / Upload, right-aligned
                  via margin-left:auto. Icon trails the text. Only rendered
                  when video audio is actually audible. */}
              {videoSrc && videoVolume > 0 && (
                <div className={styles.headphoneHint}>
                  <span>Please use headphones when video audio is on.</span>
                  <i className="ph-bold ph-headphones" />
                </div>
              )}
            </div>

            {!hasVoice && (
              <div className={styles.warning}>
                <i className="ph-bold ph-warning" />
                This asset has no voice assigned — ask the admin to set one.
              </div>
            )}

            {state.kind === 'error' && (
              <div className={styles.errorBox}>
                <i className="ph-bold ph-warning-circle" />
                {state.message}
                <button
                  type="button"
                  className={styles.errorDismiss}
                  onClick={() => setState({ kind: 'idle' })}
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Session-limit notice moved to the Params column (mid) so it
                sits next to the take-related controls and is more visible
                than buried below the actions row. See colMid below. */}

            {/* Script reference (spec §Recording Studio Overlay → Left col):
                shown only for predefined assets where the admin shipped a
                script. Custom assets edit their script on the main STS view
                so the booth doesn't double-show it. */}
            {activeAsset?.script && !activeAsset?._isCustom && (
              <div className={styles.scriptPanel}>
                <div className={styles.scriptPanelLabel}>Script</div>
                <div className={styles.scriptPanelText} {...scriptAttrs(activeAsset.script)}>{activeAsset.script}</div>
              </div>
            )}
          </div>

          {/* ── Middle column (~25%) ───────────────────────────────────── */}
          <BoothMidColumn
            isMultiVoice={isMultiVoice}
            currentVoiceId={currentVoiceId}
            onVoiceChange={setCurrentVoiceId}
            assetVoiceIds={assetVoiceIds}
            voices={configVoices ?? []}
            busy={busy}
            params={params}
            onParamsChange={setParams}
            atLimit={atLimit}
            sessionNum={sessionNum}
            takes={takes}
            activeAssetVoiceId={activeAssetVoiceId}
            hasSource={!!loadedSource}
            onGenerate={runStsGenerate}
          />

          {/* ── Right column (~25%) ────────────────────────────────────── */}
          <BoothTakesColumn
            takes={takes}
            playingTakeId={playingTakeId}
            isMultiVoice={isMultiVoice}
            assetVoiceIds={assetVoiceIds}
            setPlayingTakeId={setPlayingTakeId}
            onToggleMark={toggleMark}
            onDelete={deleteTake}
          />
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <BoothFooter
          takes={takes}
          hasUnmarked={hasUnmarked}
          busy={busy}
          onCancel={handleCancel}
          onFinish={handleFinish}
        />
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
